/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import InvoiceService from '../services/InvoiceService'
import clsx from 'clsx'

const InvoiceDetails = () => {
    const { currentUser } = useContext(AppContext)
    const { invoiceId } = useParams()
    const history = useHistory()
    
    const [invoice, setInvoice] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (invoiceId) {
            fetchInvoiceDetails()
        }
    }, [invoiceId])

    const fetchInvoiceDetails = async () => {
        setLoading(true)
        try {
            const response = await InvoiceService.getInvoiceDetails(invoiceId)
            
            if (response.success) {
                setInvoice({
                    ...response.data,
                    paymentStatus: InvoiceService.calculatePaymentStatus(response.data)
                })
            } else {
                console.error('Failed to fetch invoice details:', response.errorMessage || response.message)
                alert('Failed to load invoice details')
                history.push('/sales-invoices')
            }
        } catch (error) {
            console.error('Error fetching invoice details:', error)
            alert('Error loading invoice details')
            history.push('/sales-invoices')
        } finally {
            setLoading(false)
        }
    }

    const handlePrintInvoice = async () => {
        try {
            await InvoiceService.printInvoice(invoiceId)
        } catch (error) {
            console.error('Error printing invoice:', error)
            alert('Error printing invoice. Please try again.')
        }
    }

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading invoice details...</p>
                </div>
            </div>
        )
    }

    if (!invoice) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <h4>Invoice not found</h4>
                    <Link to="/sales-invoices" className="btn btn-primary">
                        Back to Invoices
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4">
                <div className="mb-3 mb-lg-0">
                    <div className="d-flex align-items-center">
                        <Link to="/sales-invoices" className="btn btn-link p-0 me-3">
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <div>
                            <h1 className="h3 mb-0">Invoice Details</h1>
                            <p className="text-muted mb-0">Invoice #{invoice.invoiceNo}</p>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        className="btn btn-outline-info btn-sm"
                        onClick={handlePrintInvoice}
                    >
                        <i className="fas fa-print me-1"></i>
                        <span className="d-none d-md-inline">Print Invoice</span>
                        <span className="d-md-none">Print</span>
                    </button>
                    <Link
                        to={`/sales-orders/${invoice.orderId}`}
                        className="btn btn-outline-primary btn-sm"
                    >
                        <i className="fas fa-shopping-cart me-1"></i>
                        <span className="d-none d-md-inline">View Order</span>
                        <span className="d-md-none">Order</span>
                    </Link>
                </div>
            </div>

            <div className="row">
                {/* Left Column */}
                <div className="col-lg-8">
                    {/* Invoice Summary */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Invoice Summary</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Invoice ID:</strong></td>
                                                <td>#{invoice.entityId}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Invoice No:</strong></td>
                                                <td>{invoice.invoiceNo}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Invoice Date:</strong></td>
                                                <td>{InvoiceService.formatDate(invoice.invoiceDate)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Order No:</strong></td>
                                                <td>
                                                    <Link to={`/sales-orders/${invoice.orderId}`}>
                                                        {invoice.order?.orderNo}
                                                    </Link>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Order Date:</strong></td>
                                                <td>{InvoiceService.formatDate(invoice.order?.orderDate)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Payment Status:</strong></td>
                                                <td>
                                                    <span className={clsx('badge', InvoiceService.getPaymentStatusBadge(invoice.paymentStatus))}>
                                                        {invoice.paymentStatus?.replace('_', ' ')}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Order Status:</strong></td>
                                                <td>
                                                    <span className="badge badge-info">
                                                        {invoice.order?.status}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Total Qty:</strong></td>
                                                <td>{invoice.totalQty}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Information */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Customer Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <h6>Customer Details</h6>
                                    <p className="mb-1"><strong>{invoice.order?.customer?.fullName || 'N/A'}</strong></p>
                                    <p className="mb-1">Email: {invoice.order?.customer?.email || 'N/A'}</p>
                                    <p className="mb-1">Mobile: {invoice.order?.customer?.mobileNo || 'N/A'}</p>
                                    <p className="mb-0">Group: {invoice.order?.customer?.customerGroup?.customerCroupCode || 'N/A'}</p>
                                </div>
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <h6>Shipping Address</h6>
                                    {(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress) ? (
                                        <div>
                                            <p className="mb-1">{(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).fullName || 'N/A'}</p>
                                            <p className="mb-1">{(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).street}</p>
                                            <p className="mb-1">{(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).region}, {(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).area}</p>
                                            <p className="mb-1">{(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).country?.name}</p>
                                            <p className="mb-0">Phone: {(invoice.sales_invoice_shipping_address || invoice.salesInvoiceShippingAddress).phoneNumber}</p>
                                        </div>
                                    ) : (
                                        <p>No shipping address</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="row">
                                <div className="col-12">
                                    <h6>Payment & Shipping</h6>
                                    <p className="mb-1">Payment Method: {(invoice.order?.payment_method || invoice.order?.paymentMethod)?.name || 'N/A'}</p>
                                    <p className="mb-0">Shipping Method: {(invoice.order?.shipping_method || invoice.order?.shippingMethod)?.methodName || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Items */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Invoice Items</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Price</th>
                                            <th>Qty</th>
                                            <th>Tax</th>
                                            <th>Discount</th>
                                            <th>Row Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(invoice.invoice_items || invoice.invoiceItems)?.map((item) => (
                                            <tr key={item.entityId}>
                                                <td>
                                                    <strong>{item.name}</strong>
                                                </td>
                                                <td>{item.sku}</td>
                                                <td>{InvoiceService.formatCurrency(item.price)}</td>
                                                <td>{item.qty}</td>
                                                <td>{InvoiceService.formatCurrency(item.taxAmount)}</td>
                                                <td>{InvoiceService.formatCurrency(item.discountAmount)}</td>
                                                <td>{InvoiceService.formatCurrency(item.rowTotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Customer Notes */}
                    {invoice.customerNote && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Customer Notes</h5>
                            </div>
                            <div className="card-body">
                                <p className="mb-0">{invoice.customerNote}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar */}
                <div className="col-lg-4">
                    {/* Invoice Totals */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Invoice Totals</h5>
                        </div>
                        <div className="card-body">
                            <table className="table table-borderless">
                                <tbody>
                                    <tr>
                                        <td>Subtotal:</td>
                                        <td className="text-end">{InvoiceService.formatCurrency(invoice.subtotal)}</td>
                                    </tr>
                                    <tr>
                                        <td>Tax:</td>
                                        <td className="text-end">{InvoiceService.formatCurrency(invoice.taxAmount)}</td>
                                    </tr>
                                    <tr>
                                        <td>Discount:</td>
                                        <td className="text-end text-danger">-{InvoiceService.formatCurrency(invoice.discountAmount)}</td>
                                    </tr>
                                    {invoice.promoAmount > 0 && (
                                        <tr>
                                            <td>Promo Discount:</td>
                                            <td className="text-end text-danger">-{InvoiceService.formatCurrency(invoice.promoAmount)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Shipping:</td>
                                        <td className="text-end">{InvoiceService.formatCurrency(invoice.shippingAmount)}</td>
                                    </tr>
                                    <tr>
                                        <td>Shipping Tax:</td>
                                        <td className="text-end">{InvoiceService.formatCurrency(invoice.shippingTaxAmount)}</td>
                                    </tr>
                                    <tr className="border-top">
                                        <th>Invoice Total:</th>
                                        <th className="text-end">{InvoiceService.formatCurrency(invoice.amount)}</th>
                                    </tr>
                                    <tr>
                                        <td>Total Paid:</td>
                                        <td className="text-end text-success">{InvoiceService.formatCurrency(invoice.totalPaid || 0)}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Balance Due:</strong></td>
                                        <td className="text-end"><strong>{InvoiceService.formatCurrency((invoice.amount || 0) - (invoice.totalPaid || 0))}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Payment History */}
                    {invoice.payments && invoice.payments.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Payment History</h5>
                            </div>
                            <div className="card-body">
                                {invoice.payments.map((payment) => (
                                    <div key={payment.orderPaymentId} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                        <div>
                                            <div><strong>Method:</strong> {payment.method}</div>
                                            <div className="text-muted small">{InvoiceService.formatDate(payment.paymentDate)}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="text-success fw-bold">{InvoiceService.formatCurrency(payment.amountPaid)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Payment Methods */}
                    {invoice.paymentMethods && invoice.paymentMethods.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Payment Methods Used</h5>
                            </div>
                            <div className="card-body">
                                {invoice.paymentMethods.map((method, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <div><strong>{method.name}</strong></div>
                                            {method.info && (
                                                <div className="text-muted small">{method.info}</div>
                                            )}
                                        </div>
                                        <div className="text-end">
                                            <div className="text-success">{InvoiceService.formatCurrency(method.totalPaid)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quick Actions */}
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Quick Actions</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-grid gap-2">
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handlePrintInvoice}
                                >
                                    <i className="fas fa-print me-1"></i>
                                    <span className="d-none d-md-inline">Print Invoice</span>
                                    <span className="d-md-none">Print</span>
                                </button>
                                <Link
                                    to={`/sales-orders/${invoice.orderId}`}
                                    className="btn btn-outline-primary btn-sm"
                                >
                                    <i className="fas fa-shopping-cart me-1"></i>
                                    <span className="d-none d-md-inline">View Order</span>
                                    <span className="d-md-none">Order</span>
                                </Link>
                                <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => window.location.href = `mailto:${invoice.order?.customer?.email}?subject=Invoice ${invoice.invoiceNo}`}
                                    disabled={!invoice.order?.customer?.email}
                                >
                                    <i className="fas fa-envelope me-1"></i>
                                    <span className="d-none d-md-inline">Email Customer</span>
                                    <span className="d-md-none">Email</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default InvoiceDetails 